import type { MediaKind } from "../api/mediaE2EE";
import {
  listWebMediaOutbox,
  markWebMediaOutboxFailure,
  removeWebMediaOutbox,
  enqueueMediaManifest,
} from './webMediaOutbox';
import {
  requestMediaUploadSlot,
  uploadEncryptedMediaBlob,
  createMediaManifest,
} from '../api/mediaE2EE';
import { encryptMediaBase64 } from '../media/mediaCrypto';
import { resolveRecipientPublicKeys } from '../crypto/recipientKeyResolver';
import { wrapContentKeyForRecipient } from '../crypto/recipientKeyWrap';

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}


function imageDataUrlFromBase64(mimeType: string, plainBase64: string) {
  const mime = String(mimeType || 'image/jpeg').trim() || 'image/jpeg';
  const b64 = String(plainBase64 || '').replace(/^data:[^,]+,/, '').trim();
  return `data:${mime};base64,${b64}`;
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('web_thumbnail_image_load_failed'));
    img.src = src;
  });
}

async function createWebImageThumbnailBase64(args: {
  plainBase64: string;
  mimeType: string;
  maxEdge?: number;
  quality?: number;
}): Promise<{ base64: string; width: number; height: number; mimeType: string } | null> {
  const mime = String(args.mimeType || '').toLowerCase();
  if (!mime.startsWith('image/')) return null;
  if (mime.includes('gif')) return null;

  const input = String(args.plainBase64 || '').replace(/^data:[^,]+,/, '').trim();
  if (!input) return null;

  try {
    const img = await loadImageElement(imageDataUrlFromBase64(args.mimeType, input));
    const maxEdge = Math.max(80, Math.min(720, Number(args.maxEdge || 360)));
    const sourceWidth = Math.max(1, Number(img.naturalWidth || img.width || 1));
    const sourceHeight = Math.max(1, Number(img.naturalHeight || img.height || 1));
    const scale = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(img, 0, 0, width, height);

    const outMimeType = 'image/jpeg';
    const dataUrl = canvas.toDataURL(outMimeType, Math.max(0.5, Math.min(0.9, Number(args.quality || 0.72))));
    const base64 = String(dataUrl || '').replace(/^data:[^,]+,/, '').trim();

    if (!base64) return null;

    return {
      base64,
      width,
      height,
      mimeType: outMimeType,
    };
  } catch {
    return null;
  }
}

async function buildEncryptedWebThumbnail(args: {
  plainBase64: string;
  mimeType: string;
  mediaId: string;
  recipientKeys: Array<{
    recipientUserId: string;
    recipientKeyId?: string;
    publicKeyBase64: string;
  }>;
}) {
  const thumb = await createWebImageThumbnailBase64({
    plainBase64: args.plainBase64,
    mimeType: args.mimeType,
  });

  if (!thumb) return null;

  const encryptedThumb = encryptMediaBase64(thumb.base64);
  const senderKeyId = `sender-web-thumbnail-${args.mediaId}`;

  const recipientEnvelopes = args.recipientKeys.map((rk) =>
    wrapContentKeyForRecipient({
      recipientUserId: rk.recipientUserId,
      recipientKeyId: rk.recipientKeyId,
      recipientPublicKeyBase64: rk.publicKeyBase64,
      senderKeyId,
      contentKeyBase64: encryptedThumb.wrappedContentKeyBase64,
    })
  );

  return {
    version: 1,
    encrypted: true,
    kind: 'thumbnail',
    algorithm: encryptedThumb.algorithm,
    mimeType: thumb.mimeType,
    width: thumb.width,
    height: thumb.height,
    ciphertextBase64: encryptedThumb.ciphertextBase64,
    sha256CiphertextHex: encryptedThumb.sha256CiphertextHex,
    encryption: {
      version: 1,
      algorithm: encryptedThumb.algorithm,
      keyId: `thumbnail-key-${args.mediaId}`,
      iv: encryptedThumb.ivBase64,
      wrappedContentKey: recipientEnvelopes[0]
        ? {
            scheme: recipientEnvelopes[0].scheme,
            recipientKeyId: recipientEnvelopes[0].recipientKeyId,
            senderKeyId: recipientEnvelopes[0].senderKeyId,
            wrappedKeyBase64: recipientEnvelopes[0].wrappedKeyBase64,
          }
        : undefined,
      recipientEnvelopes,
    },
    createdAt: new Date().toISOString(),
  };
}


async function flushOne(item: any) {
  const manifest = item?.manifest || item?.payload?.manifest || null;
  if (!manifest) throw new Error('missing_manifest');

  const mediaId = String(manifest?.mediaId || '').trim();
  const conversationId = String(manifest?.conversationId || '').trim();
  const mediaKind = String(manifest?.mediaKind || '').trim();
  const mimeType = String(manifest?.mimeType || '').trim();

  if (!mediaId || !conversationId || !mediaKind || !mimeType) {
    throw new Error('invalid_media_outbox_payload');
  }

  const slot: any = await requestMediaUploadSlot({
    mediaId,
    conversationId,
    mediaKind: mediaKind as MediaKind,
    mimeType,
    sizeBytes: Number(manifest?.sizeBytes || 0),
  });

  const bytes =
    typeof manifest?.ciphertextBase64 === 'string' && manifest.ciphertextBase64
      ? b64ToBytes(manifest.ciphertextBase64)
      : null;

  const finalMediaId = slot.mediaId || mediaId;
  const uploadSizeBytes = Number(bytes?.byteLength || manifest?.sizeBytes || 0);

  // The full encrypted media blob is uploaded separately below. Never include
  // ciphertextBase64 in the JSON manifest request, because that duplicates the
  // complete encrypted file and can exceed the HTTP JSON/body-size limit.
  const {
    ciphertextBase64: _encryptedBlobCiphertextBase64,
    ciphertext_base64: _encryptedBlobCiphertextBase64Snake,
    ...manifestMetadata
  } = manifest;

  // Web backend expects a manifest/media row before the blob PUT can resolve the mediaId.
  const manifestResponse: any = await createMediaManifest({
    mediaId: finalMediaId,
    conversationId,
    recipientUserId: manifest?.recipientUserId ?? null,
    mediaKind: mediaKind as MediaKind,
    mimeType,
    sizeBytes: uploadSizeBytes,
    manifestJson: {
      ...manifestMetadata,
      mediaId: finalMediaId,
      sizeBytes: uploadSizeBytes,
    },
    previewObjectKey: manifest?.previewObjectKey || null,
  });

  if (manifestResponse?.ok === false || manifestResponse?.error) {
    throw new Error(String(manifestResponse?.error || 'media_manifest_create_failed'));
  }

  if (bytes && bytes.byteLength > 0) {
    const blobResponse: any = await uploadEncryptedMediaBlob(finalMediaId, bytes);
    if (blobResponse?.ok === false || blobResponse?.error) {
      throw new Error(String(blobResponse?.error || 'media_blob_upload_failed'));
    }
  }
}

export async function enqueueMediaManifestWeb(payload: {
  mediaId: string;
  conversationId: string;
  recipientUserId?: string | null;
  mediaKind: 'image' | 'audio' | 'video';
  mimeType: string;
  sizeBytes: number;
  manifest: any;
}) {
  return enqueueMediaManifest({
    mediaId: payload.mediaId,
    conversationId: payload.conversationId,
    recipientUserId: payload.recipientUserId ?? undefined,
    mediaKind: payload.mediaKind,
    mimeType: payload.mimeType,
    sizeBytes: payload.sizeBytes,
    manifest: payload.manifest,
  });
}

export async function flushWebMediaOutbox() {
  const items = await listWebMediaOutbox();
  for (const item of items) {
    try {
      await flushOne(item);
      await removeWebMediaOutbox(item.id);
    } catch (e: any) {
      const message = e?.message || 'media_sync_failed';
      await markWebMediaOutboxFailure(item.id, message);
      throw new Error(message);
    }
  }
}


export async function enqueueEncryptedWebMediaForThread(args: {
  conversationId: string;
  recipientUserId?: string | null;
  recipientUserIds?: string[];
  mediaKind: 'image' | 'audio' | 'video';
  mimeType: string;
  originalFilename?: string;
  sizeBytes: number;
  plainBase64: string;
  temporaryMode?: "normal" | "view_once";
  visibilityMode?: "normal" | "view_once";
}) {
  const now = Date.now();
  const mediaId = `web_media_${now}_${Math.random().toString(36).slice(2, 8)}`;
  const encrypted = encryptMediaBase64(args.plainBase64);

  const recipientUserIds = Array.from(
    new Set(
      [
        ...(Array.isArray(args.recipientUserIds) ? args.recipientUserIds : []),
        ...(args.recipientUserId ? [args.recipientUserId] : []),
      ]
      .map((x) => String(x || '').trim())
      .filter(Boolean)
    )
  );

  const resolvedRecipientKeys = await resolveRecipientPublicKeys(recipientUserIds);
  const senderKeyId = 'sender-web-local-v1';

  const recipientEnvelopes = resolvedRecipientKeys.map((rk) =>
    wrapContentKeyForRecipient({
      recipientUserId: rk.recipientUserId,
      recipientKeyId: rk.recipientKeyId,
      recipientPublicKeyBase64: rk.publicKeyBase64,
      senderKeyId,
      contentKeyBase64: encrypted.wrappedContentKeyBase64,
    })
  );

  const visibilityMode =
    args.temporaryMode === 'view_once' || args.visibilityMode === 'view_once'
      ? 'view_once'
      : 'normal';

  const allowThumbnail = visibilityMode === 'view_once' ? false : true;

  const encryptedThumbnail = allowThumbnail
    ? await buildEncryptedWebThumbnail({
        plainBase64: args.plainBase64,
        mimeType: args.mimeType,
        mediaId,
        recipientKeys: resolvedRecipientKeys,
      })
    : null;

  const manifest = {
    mediaId,
    conversationId: args.conversationId,
    recipientUserId: recipientUserIds[0] || undefined,
    mediaKind: args.mediaKind,
    kind: args.mediaKind,
    mimeType: args.mimeType,
    originalFilename: args.originalFilename || undefined,
    sizeBytes: Number(args.sizeBytes || 0),
    thumbnail: encryptedThumbnail || undefined,
    ciphertextBase64: encrypted.ciphertextBase64,
    sha256CiphertextHex: encrypted.sha256CiphertextHex,
    encryption: {
      version: 1,
      algorithm: encrypted.algorithm,
      keyId: `web-local-key-${mediaId}`,
      iv: encrypted.ivBase64,
      wrappedContentKey: recipientEnvelopes[0]
        ? {
            scheme: recipientEnvelopes[0].scheme,
            recipientKeyId: recipientEnvelopes[0].recipientKeyId,
            senderKeyId: recipientEnvelopes[0].senderKeyId,
            wrappedKeyBase64: recipientEnvelopes[0].wrappedKeyBase64,
          }
        : undefined,
      recipientEnvelopes,
    },
    visibilityMode,
    visibility_mode: visibilityMode,
    allowThumbnail,
    allow_thumbnail: allowThumbnail,
    allowSave: args.temporaryMode === "view_once" ? false : true,
    allow_save: args.temporaryMode === "view_once" ? false : true,
    allowShare: args.temporaryMode === "view_once" ? false : true,
    allow_share: args.temporaryMode === "view_once" ? false : true,
    screenshotProtection: args.temporaryMode === "view_once",
    screenshot_protection: args.temporaryMode === "view_once",
    deleteAfterViewSeconds: args.temporaryMode === "view_once" ? 10 : undefined,
    delete_after_view_seconds: args.temporaryMode === "view_once" ? 10 : undefined,
    videoSinglePlay: args.temporaryMode === "view_once",
    video_single_play: args.temporaryMode === "view_once",
    policy: {
      visibilityMode,
      allowThumbnail,
      allowSave: args.temporaryMode === "view_once" ? false : true,
      allowShare: args.temporaryMode === "view_once" ? false : true,
      screenshotProtection: args.temporaryMode === "view_once",
      deleteAfterViewSeconds: args.temporaryMode === "view_once" ? 10 : undefined,
      videoSinglePlay: args.temporaryMode === "view_once",
    },
    chunks: [],
    createdAt: new Date(now).toISOString(),
  };

  const queued: any = await enqueueMediaManifestWeb({
    mediaId,
    conversationId: args.conversationId,
    recipientUserId: args.recipientUserId || undefined,
    mediaKind: args.mediaKind,
    mimeType: args.mimeType,
    sizeBytes: Number(args.sizeBytes || 0),
    manifest,
  });

  return {
    ...(queued && typeof queued === 'object' ? queued : {}),
    mediaId,
    manifest,
  };
}
